import { ArrowUpRight, Stars01 } from "@untitledui/icons";
import type { ReactNode } from "react";

const UP = "#17b26a";
const DOWN = "#d42b2b";

export type StatCardProps = {
  label: string;
  tint: string;
  accent: string;
  value: ReactNode;
  valueSuffix?: string;
  change?: number | null;
  note: ReactNode;
  onAsk: () => void;
  legend?: Array<{ name: string; color: string }>;
  footer?: { label: string; onClick: () => void };
  children?: ReactNode;
};

const formatChange = (change: number) => `${change < 0 ? "−" : "+"}${Math.abs(change).toFixed(1)}%`;

export function AskAction({ onAsk }: { onAsk: () => void }) {
  return (
    <span className="opacity-0 transition-opacity duration-150 group-hover:opacity-100 focus-within:opacity-100">
      <button
        type="button"
        onClick={onAsk}
        className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 px-1 py-1 text-xs font-semibold text-brand-600 transition-colors duration-150 hover:text-brand-700"
      >
        <Stars01 width={13} height={13} aria-hidden="true" className="ask-sparkle" />
        Ask
      </button>
    </span>
  );
}

export function StatCard({
  label,
  tint,
  accent,
  value,
  valueSuffix,
  change,
  note,
  onAsk,
  legend,
  footer,
  children,
}: StatCardProps) {
  return (
    <section
      data-testid="stat-card"
      data-card={label}
      style={{ backgroundColor: tint }}
      className="group flex flex-col gap-2.5 rounded-[14px] px-7 py-4 shadow-[0_2px_12px_rgba(66,48,125,0.07)]"
    >
      <header className="flex items-start gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <p style={{ color: accent }} className="text-[11px] font-semibold uppercase tracking-[0.1em]">
            {label}
          </p>
          <p className="flex items-baseline gap-2 text-[36px] leading-none font-semibold text-[#21133a] tabular-nums">
            <span data-testid="stat-card-value">{value}</span>
            {valueSuffix && <span className="text-[14px] font-medium text-[#21133a]/70">{valueSuffix}</span>}
            {change != null && (
              <span className="text-[14px] font-semibold" style={{ color: change < 0 ? DOWN : UP }}>
                {formatChange(change)}
              </span>
            )}
          </p>
        </div>
        <AskAction onAsk={onAsk} />
      </header>

      <p className="text-[13px] leading-5 text-[#21133a]/70">{note}</p>

      {children}

      {legend && legend.length > 0 && (
        <ul className="m-0 flex list-none flex-wrap items-center gap-x-4 gap-y-1 p-0">
          {legend.map((entry) => (
            <li key={entry.name} className="flex items-center gap-1.5 text-[12px] text-[#21133a]/70">
              <span
                aria-hidden="true"
                className="inline-block size-2 shrink-0 rounded-[2px]"
                style={{ backgroundColor: entry.color }}
              />
              {entry.name}
            </li>
          ))}
        </ul>
      )}

      {footer && (
        <button
          type="button"
          onClick={footer.onClick}
          style={{ color: accent }}
          className="mt-auto inline-flex w-full cursor-pointer items-center justify-center gap-1.5 border-t border-[#21133a]/10 pt-3 text-[15px] leading-6 font-semibold transition-opacity duration-150 hover:opacity-70"
        >
          {footer.label}
          <ArrowUpRight width={16} height={16} aria-hidden="true" className="shrink-0" />
        </button>
      )}
    </section>
  );
}

export function StatCardSkeleton() {
  return <div className="h-[320px] animate-pulse rounded-[14px] bg-gray-200" />;
}
